import {
  IStepValueReferral,
  IWizardHostState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOST_STEPS_PATHS
} from "../../../interfaces";
import { Form, Select } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserAPI } from "../../../../../API/userApi";
import { getChangedFields, getStepErrorsFromResponse } from "../../../utils";
import {
  HostReasonToBecomeHostValues,
  HostSourceToHospiValues
} from "../../../../../interfaces/interfaces";
import styled from "styled-components/macro";

const statePath = WIZARD_HOST_STEPS_PATHS.Referral;
export const WizardStepReferral: TWizardStepComponentEditable<IWizardHostState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IStepValueReferral>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] = useState<TStepErrors<IStepValueReferral>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return UserAPI.wizardHostSaveReferral(values)
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return UserAPI.wizardHostSaveReferralPartially(
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepReferralView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const SelectWithLongText = styled(Select)`
  .ant-select-selection-item {
    height: auto !important;
  }
  .ant-select-selection-item-content {
    white-space: break-spaces !important;
  }
` as typeof Select;

export const WizardStepReferralView: TWizardStepComponentEditableView<
  IStepValueReferral
> = ({ errors, values, setValues, viewonly, readonly }) => {
  const { t } = useTranslation("host");
  const { t: houset } = useTranslation("house");

  const reasonToBecomeHostOptions = HostReasonToBecomeHostValues.map((v) => ({
    value: v,
    label: (
      t(`hostProfile.referral.form.reasonToBecomeHost.options`, {
        returnObjects: true
      }) as { [key: string]: string }
    )[v]
  }));

  const sourceToHospiOptions = HostSourceToHospiValues.map((v) => ({
    value: v,
    label: (
      t(`hostProfile.referral.form.sourceToHospi.options`, {
        returnObjects: true
      }) as { [key: string]: string }
    )[v]
  }));

  return (
    <Form layout="vertical">
      <Form.Item
        label={t(`hostProfile.referral.form.reasonToBecomeHost.title`)}
        validateStatus={errors?.reason_to_become_host ? "error" : ""}
        help={
          errors?.reason_to_become_host ? houset("wizard.errors.generic") : ""
        }
      >
        {viewonly ? (
          <StepViewOnlyValue>
            {reasonToBecomeHostOptions
              .filter((o) => values.reason_to_become_host?.includes(o.value))
              .map((o) => o.label)
              ?.join(", ")}
          </StepViewOnlyValue>
        ) : (
          <SelectWithLongText
            options={reasonToBecomeHostOptions}
            value={values.reason_to_become_host ?? undefined}
            size="large"
            mode="multiple"
            disabled={readonly}
            onChange={(value) => {
              setValues({ ...values, reason_to_become_host: value });
            }}
          />
        )}
      </Form.Item>
      <Form.Item
        label={t(`hostProfile.referral.form.sourceToHospi.title`)}
        validateStatus={errors?.source_to_hospi ? "error" : ""}
        help={errors?.source_to_hospi ? houset("wizard.errors.generic") : ""}
      >
        {viewonly ? (
          <StepViewOnlyValue>
            {sourceToHospiOptions
              .filter((o) => values.source_to_hospi?.includes(o.value))
              .map((o) => o.label)
              ?.join(", ")}
          </StepViewOnlyValue>
        ) : (
          <SelectWithLongText
            options={sourceToHospiOptions}
            value={values.source_to_hospi ?? undefined}
            size="large"
            mode="multiple"
            disabled={readonly}
            onChange={(value) =>
              setValues({ ...values, source_to_hospi: value })
            }
          />
        )}
      </Form.Item>
    </Form>
  );
};
